/**
 * MapView: stateless renderer for the regional filter map. Accepts derived props
 * (geography, projection, selection lookups) and renders selectable shapes
 * across all layers: federal states, administrative districts, districts, and
 * postal codes. Interaction is routed upward via callbacks; this component owns
 * no state beyond transient hover refs to keep rendering deterministic.
 * Author: bb
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BUNDESLAENDER } from "@/services/mockData";
import { geoCentroid, geoMercator, geoPath } from "d3-geo";
import type { Coordinates } from "@vnedyalk0v/react19-simple-maps";
import { useApp } from "@/contexts/AppContext";
import {
  buildRegionSelectionKey,
  getFeatureBounds,
  getFeatureDisplayName,
  getFeatureParentState,
  getLayerGeography,
  normalizeBundeslandName,
  MapLayerId,
  GERMANY_BOUNDS,
} from "./mapHelpers";
import type { MapViewProps } from "./RegionalFilter.types";

type FeatureMeta = {
  feature: any;
  regionSelectionKey: string;
  parentState: string | null;
  featureName: string | null;
  interactive: boolean;
};

type FeatureMetaCacheEntry = {
  layerId: MapLayerId;
  meta: FeatureMeta;
};

type DensityStats = {
  minPositive: number;
  maxPositive: number;
};

type FeaturePathEntry = {
  meta: FeatureMeta;
  path: Path2D;
};

type PickEntry = FeaturePathEntry & {
  pickId: number;
  pickColor: string;
};

type Transform = {
  x: number;
  y: number;
  k: number;
};

type PostalLodLevel = "coarse" | "medium" | "full";

const MIN_POSITIVE_INDEX = 2;
const PAN_THRESHOLD_PX = 4;
const MIN_ZOOM = 0.8;
const MAX_ZOOM = 20;

const resolvePostalLod = (k: number): PostalLodLevel => {
  if (k < 4) return "coarse";
  if (k < 8) return "medium";
  return "full";
};

// Pick the most readable label from arbitrary feature properties.
const getFeatureHoverLabel = (
  feature: any,
  primary: string | null,
  secondary?: string | null
): string | null => {
  const primaryLabel = primary?.toString().trim();
  if (primaryLabel) return primaryLabel;
  const secondaryLabel = secondary?.toString().trim();
  if (secondaryLabel) return secondaryLabel;

  const props = feature?.properties ?? {};
  const rawCandidates = [...Object.values(props), feature?.id ?? null];

  const scored = rawCandidates
    .map((value) => {
      if (value == null) return null;
      const label = String(value).trim();
      if (!label || /^relation\//i.test(label)) return null;
      const hasLetters = /[A-Za-z]/.test(label);
      const hasDigits = /\d/.test(label);
      const lengthScore =
        label.length >= 3 && label.length <= 60
          ? 3
          : label.length <= 2 || label.length > 80
          ? 0
          : 1;
      const tokenScore = Math.max(0, 3 - label.split(/\s+/).length);
      const mixedScore = hasLetters && hasDigits ? 2 : hasLetters ? 3 : 1;
      const separatorBonus = /[,–-]/.test(label) ? 1 : 0;
      const score =
        lengthScore + tokenScore + mixedScore + separatorBonus + (hasDigits ? 0.5 : 0);
      return { label, score };
    })
    .filter(Boolean) as Array<{ label: string; score: number }>;

  if (!scored.length) return null;
  scored.sort((a, b) => b.score - a.score || a.label.length - b.label.length);
  return scored[0]?.label ?? null;
};

const getFeatureCentroid = (feature: any): Coordinates | null => {
  try {
    const [x, y] = geoCentroid(feature);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return [x, y] as Coordinates;
    }
  } catch {
    /* noop */
  }
  return null;
};

const GERMANY_AREA =
  (GERMANY_BOUNDS.maxLon - GERMANY_BOUNDS.minLon) *
  (GERMANY_BOUNDS.maxLat - GERMANY_BOUNDS.minLat);

const getFeatureLabelFontSize = (feature: any, layerId: string): number => {
  const base =
    layerId === "federalStates"
      ? 12
      : layerId === "administrativeDistricts"
      ? 11
      : layerId === "districts"
      ? 10
      : 9;

  const bounds = getFeatureBounds(feature);
  if (!bounds) return base;

  const area =
    (bounds.maxLon - bounds.minLon) * (bounds.maxLat - bounds.minLat);
  if (!Number.isFinite(area) || area <= 0) return base;

  const ratio = Math.max(area / GERMANY_AREA, 1e-6);
  const adjustment = Math.log10(ratio / 0.001) * 2;
  const size = base + adjustment;
  return Math.max(base - 3, Math.min(base + 4, size));
};

const DENSITY_SCALE = Array.from({ length: 10 }, (_, index) =>
  `--map-density-${index + 1}`
);

const buildFeatureMeta = (
  detailLayer: MapLayerId,
  feature: any,
  index: number
): FeatureMeta => {
  const parentStateRaw = getFeatureParentState(detailLayer, feature);
  const parentState =
    normalizeBundeslandName(parentStateRaw) ?? parentStateRaw ?? null;
  const featureName = getFeatureDisplayName(detailLayer, feature);
  const regionSelectionKey =
    detailLayer === "postalCodes"
      ? buildRegionSelectionKey("postalCodes", feature, parentState)
      : buildRegionSelectionKey(detailLayer, feature, parentState);
  const interactive =
    !!parentState &&
    BUNDESLAENDER.includes(parentState as (typeof BUNDESLAENDER)[number]);

  return {
    feature,
    regionSelectionKey: regionSelectionKey || `region-${index}`,
    parentState,
    featureName,
    interactive,
  };
};

const resolveDensityIndex = (
  count: number,
  minPositive: number,
  maxPositive: number
) => {
  if (!Number.isFinite(count) || count <= 0 || maxPositive <= 0) {
    return 0;
  }

  const maxIndex = DENSITY_SCALE.length - 1;
  const minIndex = Math.min(MIN_POSITIVE_INDEX, maxIndex);
  if (maxPositive === minPositive) {
    return minIndex + Math.floor((maxIndex - minIndex) / 2);
  }

  const safeCount = Math.max(count, minPositive);
  const logMin = Math.log(minPositive);
  const logMax = Math.log(maxPositive);
  const logValue = Math.log(safeCount);
  const normalized = (logValue - logMin) / (logMax - logMin);
  const rawIndex = minIndex + Math.round(normalized * (maxIndex - minIndex));
  return Math.max(minIndex, Math.min(maxIndex, rawIndex));
};

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const encodePickColor = (id: number) => {
  const r = id & 0xff;
  const g = (id >> 8) & 0xff;
  const b = (id >> 16) & 0xff;
  return `rgb(${r}, ${g}, ${b})`;
};

const getCssVar = (
  styles: CSSStyleDeclaration,
  name: string,
  fallback: string
) => {
  const value = styles.getPropertyValue(name).trim();
  return value || fallback;
};

const readMapPalette = (container: HTMLElement | null) => {
  const styles = container ? getComputedStyle(container) : null;
  const fallback = styles?.color?.trim() || "currentColor";
  const outline = styles ? getCssVar(styles, "--map-outline", fallback) : fallback;
  const selected = styles
    ? getCssVar(styles, "--map-selected", fallback)
    : fallback;
  const hoverLabelFill = styles
    ? getCssVar(styles, "--map-label-fill", fallback)
    : fallback;
  const hoverLabelStroke = styles
    ? getCssVar(styles, "--map-label-stroke", fallback)
    : fallback;
  const densityFallback = styles
    ? getCssVar(styles, "--map-deselected", outline)
    : outline;
  const densities = DENSITY_SCALE.map((token) =>
    styles ? getCssVar(styles, token, densityFallback) : densityFallback
  );
  return {
    outline,
    selected,
    hoverLabelFill,
    hoverLabelStroke,
    densities,
  };
};

const applyTransform = (
  ctx: CanvasRenderingContext2D,
  transform: Transform,
  ratio: number
) => {
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.translate(transform.x, transform.y);
  ctx.scale(transform.k, transform.k);
};

const clearCanvas = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

const getCanvasPoint = (
  event: React.PointerEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement,
  width: number,
  height: number
) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const x = ((event.clientX - rect.left) / rect.width) * width;
  const y = ((event.clientY - rect.top) / rect.height) * height;
  return { x, y, rect };
};

const transformPoint = (point: [number, number], transform: Transform) => {
  return {
    x: transform.x + transform.k * point[0],
    y: transform.y + transform.k * point[1],
  };
};

const zoomAtPoint = (transform: Transform, scale: number, point: { x: number; y: number }) => {
  const nextK = clamp(transform.k * scale, MIN_ZOOM, MAX_ZOOM);
  const ratio = nextK / transform.k;
  return {
    k: nextK,
    x: point.x - (point.x - transform.x) * ratio,
    y: point.y - (point.y - transform.y) * ratio,
  };
};

/**
 * Stateless renderer for the regional map. All selection and projection logic
 * is supplied by the parent hook to keep rendering deterministic.
 */
export const MapView: React.FC<MapViewProps> = ({
  width,
  height,
  projectionCenter,
  projectionScale,
  zoomGroupKey,
  handleZoomFilter,
  geography,
  allLayers,
  detailLayer,
  activeState,
  hoveredState,
  setHoveredState,
  handleSelection,
  regionSelectionsByState,
  selectedStateSet,
  hasAllSelected,
  regionCounts,
  hoverLabelByRegionKey,
}) => {
  const { activeTheme } = useApp();
  const hoveredRef = useRef<string | null>(hoveredState);
  const hoverLabelRef = useRef<SVGTextElement | null>(null);
  const hoverLabelDataRef = useRef<{
    key: string;
    text: string;
    fontSize: number;
    point: [number, number];
    interactive: boolean;
  } | null>(null);
  const featureMetaCacheRef = useRef<WeakMap<any, FeatureMetaCacheEntry>>(
    new WeakMap()
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pickCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pickContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const devicePixelRatioRef = useRef(1);
  const transformRef = useRef<Transform>({ x: 0, y: 0, k: 1 });
  const activePointersRef = useRef(
    new Map<
      number,
      {
        x: number;
        y: number;
        clientX: number;
        clientY: number;
        scaleX: number;
        scaleY: number;
      }
    >()
  );
  const isPanningRef = useRef(false);
  const pinchStateRef = useRef<{
    startDistance: number;
    startTransform: Transform;
  } | null>(null);
  const dragStartRef = useRef<{
    clientX: number;
    clientY: number;
    scaleX: number;
    scaleY: number;
    startTransform: Transform;
  } | null>(null);
  const dragMovedRef = useRef(false);
  const hoverRAFRef = useRef<number | null>(null);
  const panRAFRef = useRef<number | null>(null);
  const hoverPointRef = useRef<{ x: number; y: number } | null>(null);
  const redrawRAFRef = useRef<number | null>(null);
  const pickLookupRef = useRef<Map<number, FeatureMeta>>(new Map());
  const pathCacheRef = useRef<Map<string, Path2D>>(new Map());
  const [postalLod, setPostalLod] = useState<PostalLodLevel>(() =>
    resolvePostalLod(1)
  );

  const safeRegionCounts = useMemo(
    () => regionCounts ?? new Map<string, number>(),
    [regionCounts]
  );

  const postalLodLayers = useMemo(
    () => ({
      coarse: allLayers.postalCodesCoarse ?? allLayers.postalCodes,
      medium: allLayers.postalCodesMedium ?? allLayers.postalCodes,
      full: allLayers.postalCodesFull ?? allLayers.postalCodes,
    }),
    [allLayers.postalCodes, allLayers.postalCodesCoarse, allLayers.postalCodesFull, allLayers.postalCodesMedium]
  );

  const effectiveGeography = useMemo(() => {
    if (detailLayer !== "postalCodes") return geography;
    const source = postalLodLayers[postalLod];
    if (!source) {
      return { type: "FeatureCollection", features: [] as any[] };
    }
    return getLayerGeography("postalCodes", activeState, {
      ...allLayers,
      postalCodes: source,
    });
  }, [activeState, allLayers, detailLayer, geography, postalLod, postalLodLayers]);

  const rawFeatures = useMemo(
    () =>
      Array.isArray((effectiveGeography as any)?.features)
        ? (effectiveGeography as any).features
        : [],
    [effectiveGeography]
  );

  const visibleFeatures = useMemo(() => {
    if (!activeState || detailLayer !== "federalStates") {
      return rawFeatures;
    }
    const target = normalizeBundeslandName(activeState);
    if (!target) return [];
    return rawFeatures.filter((feature: any) => {
      const name = feature?.properties?.name ?? feature?.properties?.NAME_1;
      const normalized = normalizeBundeslandName(name);
      return normalized === target;
    });
  }, [activeState, detailLayer, rawFeatures]);

  const featureMetaList = useMemo<FeatureMeta[]>(() => {
    const cache = featureMetaCacheRef.current;
    return visibleFeatures.map((feature: any, index: number) => {
      const cached = cache.get(feature);
      if (cached && cached.layerId === detailLayer) {
        return cached.meta;
      }
      const meta = buildFeatureMeta(detailLayer, feature, index);
      cache.set(feature, { layerId: detailLayer, meta });
      return meta;
    });
  }, [detailLayer, visibleFeatures]);

  const featureMetaByKey = useMemo(() => {
    const map = new Map<string, FeatureMeta>();
    featureMetaList.forEach((meta: FeatureMeta) => {
      map.set(meta.regionSelectionKey, meta);
    });
    return map;
  }, [featureMetaList]);

  const densityStats = useMemo<DensityStats>(() => {
    let minPositive = 0;
    let maxPositive = 0;
    featureMetaList.forEach((meta: FeatureMeta) => {
      const count = safeRegionCounts.get(meta.regionSelectionKey) ?? 0;
      if (count <= 0) return;
      if (minPositive === 0 || count < minPositive) minPositive = count;
      if (count > maxPositive) maxPositive = count;
    });
    return { minPositive, maxPositive };
  }, [featureMetaList, safeRegionCounts]);

  const projection = useMemo(() => {
    return geoMercator()
      .center(projectionCenter)
      .scale(projectionScale)
      .translate([width / 2, height / 2]);
  }, [height, projectionCenter, projectionScale, width]);

  const projectionKey = useMemo(
    () =>
      `${projectionCenter[0]}-${projectionCenter[1]}-${projectionScale}-${width}-${height}`,
    [projectionCenter, projectionScale, width, height]
  );

  const pathGenerator = useMemo(() => geoPath(projection), [projection]);

  useEffect(() => {
    pathCacheRef.current.clear();
  }, [detailLayer, postalLod, projectionKey]);

  const featurePaths = useMemo<FeaturePathEntry[]>(() => {
    const cacheKeyPrefix =
      detailLayer === "postalCodes" ? `postal:${postalLod}` : detailLayer;
    const cache = pathCacheRef.current;
    const entries: FeaturePathEntry[] = [];
    featureMetaList.forEach((meta) => {
      const cacheKey = `${cacheKeyPrefix}:${projectionKey}:${meta.regionSelectionKey}`;
      let path = cache.get(cacheKey) ?? null;
      if (!path) {
        const pathD = pathGenerator(meta.feature);
        if (!pathD) return;
        path = new Path2D(pathD);
        cache.set(cacheKey, path);
      }
      entries.push({ meta, path });
    });
    return entries;
  }, [detailLayer, featureMetaList, pathGenerator, postalLod, projectionKey]);

  const featurePathByKey = useMemo(() => {
    const map = new Map<string, Path2D>();
    featurePaths.forEach((entry) => {
      map.set(entry.meta.regionSelectionKey, entry.path);
    });
    return map;
  }, [featurePaths]);

  const pickEntries = useMemo<PickEntry[]>(() => {
    return featurePaths.map((entry, index) => ({
      ...entry,
      pickId: index + 1,
      pickColor: encodePickColor(index + 1),
    }));
  }, [featurePaths]);

  useEffect(() => {
    const lookup = new Map<number, FeatureMeta>();
    pickEntries.forEach((entry) => {
      lookup.set(entry.pickId, entry.meta);
    });
    pickLookupRef.current = lookup;
  }, [pickEntries]);

  const updateCanvasSize = useCallback(() => {
    const ratio =
      typeof window !== "undefined" && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1;
    devicePixelRatioRef.current = ratio;
    const canvases = [baseCanvasRef.current, overlayCanvasRef.current];
    canvases.forEach((canvas) => {
      if (!canvas) return;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
    });

    if (!pickCanvasRef.current) {
      pickCanvasRef.current = document.createElement("canvas");
    }
    pickCanvasRef.current.width = Math.round(width * ratio);
    pickCanvasRef.current.height = Math.round(height * ratio);
    pickContextRef.current = pickCanvasRef.current.getContext("2d", {
      willReadFrequently: true,
    });
  }, [height, width]);

  useEffect(() => {
    updateCanvasSize();
  }, [updateCanvasSize]);

  const getFeatureSelection = useCallback(
    (meta: FeatureMeta) => {
      const regionSelections = meta.parentState
        ? regionSelectionsByState.get(meta.parentState)
        : undefined;
      const hasRegionSelectionForState = (regionSelections?.size ?? 0) > 0;
      const stateSelected =
        !!meta.parentState && (selectedStateSet?.has(meta.parentState) ?? false);
      const selected =
        detailLayer === "federalStates"
          ? stateSelected
          : hasRegionSelectionForState
          ? regionSelections?.has(meta.regionSelectionKey) ?? false
          : stateSelected;
      const highlightSelected = selected && !hasAllSelected;
      return { selected, highlightSelected };
    },
    [
      detailLayer,
      hasAllSelected,
      regionSelectionsByState,
      selectedStateSet,
    ]
  );

  const drawBaseLayer = useCallback(() => {
    const canvas = baseCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    clearCanvas(ctx, canvas);
    const palette = readMapPalette(container);
    applyTransform(ctx, transformRef.current, devicePixelRatioRef.current);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const strokeScale = Math.max(transformRef.current.k, 1e-6);

    featurePaths.forEach((entry) => {
      const count = safeRegionCounts.get(entry.meta.regionSelectionKey) ?? 0;
      const densityIndex = resolveDensityIndex(
        count,
        densityStats.minPositive,
        densityStats.maxPositive
      );
      ctx.fillStyle = palette.densities[densityIndex] ?? palette.densities[0];
      ctx.fill(entry.path);
    });

    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = 0.9 / strokeScale;
    featurePaths.forEach((entry) => {
      ctx.stroke(entry.path);
    });
  }, [densityStats.maxPositive, densityStats.minPositive, featurePaths, safeRegionCounts]);

  const drawPickLayer = useCallback(() => {
    const pickCanvas = pickCanvasRef.current;
    const ctx = pickContextRef.current;
    if (!pickCanvas || !ctx) return;
    clearCanvas(ctx, pickCanvas);
    applyTransform(ctx, transformRef.current, devicePixelRatioRef.current);

    pickEntries.forEach((entry) => {
      if (!entry.meta.interactive) return;
      ctx.fillStyle = entry.pickColor;
      ctx.fill(entry.path);
    });
  }, [pickEntries]);

  const drawOverlayLayer = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    clearCanvas(ctx, canvas);
    const palette = readMapPalette(container);
    applyTransform(ctx, transformRef.current, devicePixelRatioRef.current);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const strokeScale = Math.max(transformRef.current.k, 1e-6);

    featurePaths.forEach((entry) => {
      const { highlightSelected } = getFeatureSelection(entry.meta);
      if (!highlightSelected) return;
      ctx.strokeStyle = palette.selected;
      ctx.lineWidth = 2.1 / strokeScale;
      ctx.stroke(entry.path);
    });

    const hoveredKey = hoveredRef.current;
    if (hoveredKey) {
      const hoveredMeta = featureMetaByKey.get(hoveredKey);
      const hoveredPath = hoveredMeta
        ? featurePathByKey.get(hoveredMeta.regionSelectionKey)
        : null;
      if (hoveredMeta && hoveredPath && hoveredMeta.interactive) {
        const { highlightSelected } = getFeatureSelection(hoveredMeta);
        ctx.strokeStyle = highlightSelected ? palette.selected : palette.outline;
        ctx.lineWidth = (highlightSelected ? 3.2 : 2.2) / strokeScale;
        ctx.stroke(hoveredPath);
      }
    }
  }, [
    featureMetaByKey,
    featurePathByKey,
    featurePaths,
    getFeatureSelection,
  ]);

  const updateHoverLabel = useCallback(() => {
    const labelEl = hoverLabelRef.current;
    if (!labelEl) return;
    const data = hoverLabelDataRef.current;
    if (!data) {
      labelEl.textContent = "";
      labelEl.setAttribute("opacity", "0");
      return;
    }

    const palette = readMapPalette(containerRef.current);
    const { x, y } = transformPoint(data.point, transformRef.current);
    const scale = Math.max(transformRef.current.k, 1e-6);
    labelEl.textContent = data.text;
    labelEl.setAttribute("x", x.toFixed(2));
    labelEl.setAttribute("y", y.toFixed(2));
    labelEl.setAttribute("font-size", (data.fontSize * scale).toFixed(2));
    labelEl.setAttribute("stroke-width", (2.4 * scale).toFixed(2));
    labelEl.setAttribute("fill", palette.hoverLabelFill);
    labelEl.setAttribute("stroke", palette.hoverLabelStroke);
    labelEl.setAttribute("opacity", "1");
  }, []);

  const drawAll = useCallback(() => {
    drawBaseLayer();
    drawPickLayer();
    drawOverlayLayer();
    updateHoverLabel();
  }, [drawBaseLayer, drawOverlayLayer, drawPickLayer, updateHoverLabel]);

  const scheduleDrawAll = useCallback(() => {
    if (redrawRAFRef.current != null) return;
    redrawRAFRef.current = window.requestAnimationFrame(() => {
      redrawRAFRef.current = null;
      drawAll();
    });
  }, [drawAll]);

  useEffect(() => {
    scheduleDrawAll();
  }, [activeTheme, scheduleDrawAll]);

  useEffect(() => {
    drawAll();
  }, [drawAll]);

  useEffect(() => {
    hoveredRef.current = hoveredState;
    if (!hoveredState) {
      hoverLabelDataRef.current = null;
      updateHoverLabel();
      drawOverlayLayer();
      return;
    }
    const hoverMeta = featureMetaByKey.get(hoveredState) ?? null;
    if (!hoverMeta || !hoverMeta.interactive) {
      hoverLabelDataRef.current = null;
      updateHoverLabel();
      drawOverlayLayer();
      return;
    }
    const centroid = getFeatureCentroid(hoverMeta.feature);
    const hoverLabel =
      hoverLabelByRegionKey?.[hoveredState] ??
      getFeatureHoverLabel(
        hoverMeta.feature,
        hoverMeta.featureName,
        hoverMeta.parentState
      );
    if (!centroid || !hoverLabel) {
      hoverLabelDataRef.current = null;
      updateHoverLabel();
      drawOverlayLayer();
      return;
    }
    const projected = projection(centroid as [number, number]);
    if (!projected || projected.length < 2) {
      hoverLabelDataRef.current = null;
      updateHoverLabel();
      drawOverlayLayer();
      return;
    }
    const hoverFontSize = getFeatureLabelFontSize(hoverMeta.feature, detailLayer);
    hoverLabelDataRef.current = {
      key: hoverMeta.regionSelectionKey,
      text: hoverLabel,
      fontSize: hoverFontSize,
      point: [projected[0], projected[1]],
      interactive: hoverMeta.interactive,
    };
    updateHoverLabel();
    drawOverlayLayer();
  }, [
    detailLayer,
    drawOverlayLayer,
    featureMetaByKey,
    hoverLabelByRegionKey,
    hoveredState,
    projection,
    updateHoverLabel,
  ]);


  const hitTest = useCallback(
    (point: { x: number; y: number }) => {
      const ctx = pickContextRef.current;
      if (!ctx) return null;
      const ratio = devicePixelRatioRef.current;
      const px = Math.round(point.x * ratio);
      const py = Math.round(point.y * ratio);
      if (
        px < 0 ||
        py < 0 ||
        px >= ctx.canvas.width ||
        py >= ctx.canvas.height
      ) {
        return null;
      }
      const data = ctx.getImageData(px, py, 1, 1).data;
      const id = data[0] + (data[1] << 8) + (data[2] << 16);
      if (id === 0) return null;
      const meta = pickLookupRef.current.get(id) ?? null;
      return meta && meta.interactive ? meta : null;
    },
    []
  );

  const updateCanvasCursor = useCallback((interactive: boolean) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    canvas.style.cursor = interactive ? "pointer" : "default";
  }, []);

  const schedulePanDraw = useCallback(() => {
    if (panRAFRef.current != null) return;
    panRAFRef.current = window.requestAnimationFrame(() => {
      panRAFRef.current = null;
      drawBaseLayer();
      drawOverlayLayer();
      updateHoverLabel();
    });
  }, [drawBaseLayer, drawOverlayLayer, updateHoverLabel]);

  const updatePostalLod = useCallback((k: number) => {
    const next = resolvePostalLod(k);
    setPostalLod((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    transformRef.current = { x: 0, y: 0, k: 1 };
    activePointersRef.current.clear();
    dragStartRef.current = null;
    pinchStateRef.current = null;
    dragMovedRef.current = false;
    isPanningRef.current = false;
    setPostalLod(resolvePostalLod(1));
  }, [zoomGroupKey]);

  const handleHover = useCallback(
    (meta: FeatureMeta | null) => {
      if (!meta) {
        if (hoveredRef.current === null) return;
        setHoveredState(null);
        updateCanvasCursor(false);
        return;
      }
      if (!meta.interactive) return;
      if (hoveredRef.current === meta.regionSelectionKey) return;
      setHoveredState(meta.regionSelectionKey);
      updateCanvasCursor(true);
    },
    [setHoveredState, updateCanvasCursor]
  );

  const handleRegionSelect = useCallback(
    (
      event:
        | React.PointerEvent<HTMLCanvasElement>
        | React.PointerEvent<SVGTextElement>
        | React.TouchEvent<SVGTextElement>,
      meta: FeatureMeta | null
    ) => {
      if (!meta?.interactive || !meta.parentState) return;
      if ((event as React.PointerEvent).shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      handleSelection(
        meta.parentState,
        detailLayer === "federalStates" ? undefined : meta.regionSelectionKey
      );
    },
    [detailLayer, handleSelection]
  );

  const handleCanvasPointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = overlayCanvasRef.current;
      if (!canvas) return;
      const point = getCanvasPoint(event, canvas, width, height);
      if (!point) return;
      const scaleX = point.rect.width / width;
      const scaleY = point.rect.height / height;

      const isDragging = dragStartRef.current != null;
      const activePointers = activePointersRef.current;
      if (isDragging && activePointers.size > 0 && dragStartRef.current) {
        event.preventDefault();
        activePointers.set(event.pointerId, {
          x: point.x,
          y: point.y,
          clientX: event.clientX,
          clientY: event.clientY,
          scaleX,
          scaleY,
        });
        if (pinchStateRef.current && activePointers.size >= 2) {
          const [first, second] = Array.from(activePointers.values());
          const mid = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
          const distance = Math.hypot(first.x - second.x, first.y - second.y);
          const startDistance = pinchStateRef.current.startDistance;
          const scale = startDistance > 0 ? distance / startDistance : 1;
          dragMovedRef.current = true;
          isPanningRef.current = false;
          transformRef.current = zoomAtPoint(
            pinchStateRef.current.startTransform,
            scale,
            mid
          );
          if (detailLayer === "postalCodes") {
            updatePostalLod(transformRef.current.k);
          }
          scheduleDrawAll();
          return;
        }

        if (activePointers.size > 1) {
          return;
        }

        if (activePointers.size === 1) {
          const dragStart = dragStartRef.current;
          const dxCss = event.clientX - dragStart.clientX;
          const dyCss = event.clientY - dragStart.clientY;
          if (!dragMovedRef.current) {
            const distance = Math.hypot(dxCss, dyCss);
            if (distance <= PAN_THRESHOLD_PX) {
              return;
            }
            dragMovedRef.current = true;
          }
          const dx = dxCss / dragStart.scaleX;
          const dy = dyCss / dragStart.scaleY;
          isPanningRef.current = true;
          transformRef.current = {
            ...dragStart.startTransform,
            x: dragStart.startTransform.x + dx,
            y: dragStart.startTransform.y + dy,
          };
          schedulePanDraw();
          return;
        }
      }

      if (isPanningRef.current) return;
      hoverPointRef.current = { x: point.x, y: point.y };
      if (hoverRAFRef.current != null) return;
      hoverRAFRef.current = window.requestAnimationFrame(() => {
        hoverRAFRef.current = null;
        const hoverPoint = hoverPointRef.current;
        if (!hoverPoint) return;
        const hit = hitTest(hoverPoint);
        handleHover(hit);
      });
    },
    [
      detailLayer,
      handleHover,
      height,
      hitTest,
      scheduleDrawAll,
      schedulePanDraw,
      updatePostalLod,
      width,
    ]
  );

  const handleCanvasPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (hoveredRef.current === null) return;
      const labelEl = hoverLabelRef.current;
      if (labelEl) {
        const rect = labelEl.getBoundingClientRect();
        const { clientX, clientY } = event;
        if (
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom
        ) {
          return;
        }
      }
      setHoveredState(null);
      hoverPointRef.current = null;
      updateCanvasCursor(false);
    },
    [setHoveredState, updateCanvasCursor]
  );

  const handleCanvasPointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (event.button !== 0) return;
      const canvas = overlayCanvasRef.current;
      if (!canvas) return;
      const point = getCanvasPoint(event, canvas, width, height);
      if (!point) return;

      const scaleX = point.rect.width / width;
      const scaleY = point.rect.height / height;
      activePointersRef.current.set(event.pointerId, {
        x: point.x,
        y: point.y,
        clientX: event.clientX,
        clientY: event.clientY,
        scaleX,
        scaleY,
      });
      dragMovedRef.current = false;

      if (!handleZoomFilter(event)) return;
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      if (activePointersRef.current.size === 1) {
        dragStartRef.current = {
          clientX: event.clientX,
          clientY: event.clientY,
          scaleX,
          scaleY,
          startTransform: { ...transformRef.current },
        };
        pinchStateRef.current = null;
      } else if (activePointersRef.current.size === 2) {
        const [first, second] = Array.from(activePointersRef.current.values());
        pinchStateRef.current = {
          startDistance: Math.hypot(first.x - second.x, first.y - second.y),
          startTransform: { ...transformRef.current },
        };
        dragStartRef.current = {
          clientX: event.clientX,
          clientY: event.clientY,
          scaleX,
          scaleY,
          startTransform: { ...transformRef.current },
        };
      }
    },
    [handleZoomFilter, height, width]
  );

  const handleCanvasPointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = overlayCanvasRef.current;
      if (!canvas) return;
      activePointersRef.current.delete(event.pointerId);

      if (dragStartRef.current) {
        canvas.releasePointerCapture(event.pointerId);
        if (activePointersRef.current.size === 1 && handleZoomFilter(event)) {
          const [remaining] = Array.from(activePointersRef.current.values());
          dragStartRef.current = {
            clientX: remaining.clientX,
            clientY: remaining.clientY,
            scaleX: remaining.scaleX,
            scaleY: remaining.scaleY,
            startTransform: { ...transformRef.current },
          };
          pinchStateRef.current = null;
        }
        if (activePointersRef.current.size === 1 && !handleZoomFilter(event)) {
          dragStartRef.current = null;
          pinchStateRef.current = null;
        }
        if (activePointersRef.current.size === 0) {
          dragStartRef.current = null;
          pinchStateRef.current = null;
        }
      }

      if (dragMovedRef.current) {
        dragMovedRef.current = false;
        if (dragStartRef.current) {
          const dragStart = dragStartRef.current;
          const dxCss = event.clientX - dragStart.clientX;
          const dyCss = event.clientY - dragStart.clientY;
          const dx = dxCss / dragStart.scaleX;
          const dy = dyCss / dragStart.scaleY;
          transformRef.current = {
            ...dragStart.startTransform,
            x: dragStart.startTransform.x + dx,
            y: dragStart.startTransform.y + dy,
          };
        }
        isPanningRef.current = false;
        scheduleDrawAll();
        return;
      }

      const point = getCanvasPoint(event, canvas, width, height);
      if (!point) return;
      const hit = hitTest({ x: point.x, y: point.y });
      if (!hit) return;
      handleRegionSelect(event, hit);
    },
    [handleRegionSelect, handleZoomFilter, height, hitTest, scheduleDrawAll, width]
  );

  const handleCanvasPointerCancel = useCallback(() => {
    activePointersRef.current.clear();
    dragStartRef.current = null;
    pinchStateRef.current = null;
    dragMovedRef.current = false;
    isPanningRef.current = false;
    scheduleDrawAll();
  }, [scheduleDrawAll]);

  const handleWheelEvent = useCallback(
    (event: WheelEvent) => {
      if (!handleZoomFilter(event)) return;
      isPanningRef.current = false;
      event.preventDefault();
      const canvas = overlayCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const point = {
        x: ((event.clientX - rect.left) / rect.width) * width,
        y: ((event.clientY - rect.top) / rect.height) * height,
      };
      const zoomFactor = Math.exp(-event.deltaY * 0.0025);
      transformRef.current = zoomAtPoint(transformRef.current, zoomFactor, point);
      if (detailLayer === "postalCodes") {
        updatePostalLod(transformRef.current.k);
      }
      scheduleDrawAll();
    },
    [detailLayer, handleZoomFilter, height, scheduleDrawAll, updatePostalLod, width]
  );

  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    canvas.addEventListener("wheel", handleWheelEvent, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", handleWheelEvent);
    };
  }, [handleWheelEvent]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        aspectRatio: `${width} / ${height}`,
      }}
    >
      <canvas
        ref={baseCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
      <canvas
        ref={overlayCanvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          touchAction: "manipulation",
          cursor: "default",
        }}
        onPointerDown={handleCanvasPointerDown}
        onPointerMove={handleCanvasPointerMove}
        onPointerUp={handleCanvasPointerUp}
        onPointerLeave={handleCanvasPointerLeave}
        onPointerCancel={handleCanvasPointerCancel}
      />
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <text
          ref={hoverLabelRef}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontWeight={600}
          strokeWidth={2.4}
          paintOrder="stroke"
          style={{
            overflow: "visible",
            pointerEvents: "none",
            userSelect: "none",
            cursor: "pointer",
          }}
        />
      </svg>
    </div>
  );
};
